// integrity.ts — Integrity Layer (Project Health) client.
// See workers/core-os/src/routes/integrity.ts. One unified findings queue fed
// by every catcher (completeness, plan-review bridge, triangulation, AI sweep).

import { request } from './request'

export type FindingSeverity = 'high' | 'medium' | 'low'

export interface IntegrityFinding {
  id: string
  source: string // completeness | ai_review | triangulation | invariant | human | inspector
  kind: string
  severity: FindingSeverity
  message: string
  evidence: unknown
  check_key: string | null
  target_entity_table: string | null
  target_entity_id: string | null
  suggested_action: string | null
  status: string
  resolved_at: string | null
  resolved_notes: string | null
  created_at: string
  updated_at: string
}

export interface FindingsResponse {
  ok: true
  count: number
  findings: IntegrityFinding[]
}

export interface IntegrityRun {
  id: string
  catchers_json: string | null
  status: string
  finding_count: number
  new_count: number
  reopened_count: number
  resolved_count: number
  error_text: string | null
  started_at: string
  finished_at: string | null
}

export interface RunResponse {
  ok: boolean
  run_id: string
  catchers_run: string[]
  catchers_skipped: string[]
  newCount: number
  reopenedCount: number
  resolvedCount: number
  error: string | null
}

export type FindingDisposition = 'accepted' | 'resolved' | 'rejected'

export interface LearnedCheck {
  id: string
  source_finding_id: string | null
  origin_source: string | null
  kind: string | null
  severity: string | null
  message: string
  suggested_check: string | null
  status: string
  implemented_check_key: string | null
  created_at: string
}

export const integrityApi = {
  findings: (projectId: string, params?: { status?: string; source?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.source) qs.set('source', params.source)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<FindingsResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/findings${suffix}`,
    )
  },
  run: (projectId: string, catchers?: string[]) =>
    request<RunResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/integrity/run`,
      { method: 'POST', body: JSON.stringify(catchers ? { catchers } : {}) },
    ),
  runs: (projectId: string) =>
    request<{ ok: true; runs: IntegrityRun[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/integrity/runs`,
    ),
  learnedChecks: (projectId: string, status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    return request<{ ok: true; learned_checks: LearnedCheck[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/integrity/learned-checks${qs}`,
    )
  },
  disposition: (
    findingId: string,
    body: { status: FindingDisposition; notes?: string },
  ) =>
    request<{ ok: true; id: string; status: string }>(
      `/api/findings/${encodeURIComponent(findingId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
}
